import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';

const InvoicePreview = ({ invoicePreviewData }) => {
  const {
    invoiceNumber = '01234',
    date = '11.02.2030',
    dueDate = '11.03.2030',
    issuedTo = {
      name: 'Richard Sanchez',
      company: 'Thynk Unlimited',
      address: '123 Anywhere St., Any City',
    },
    payTo = {
      bank: 'Borcele Bank',
      accountName: 'Adeline Palmerston',
      accountNumber: '0123 4567 8901',
    },
    items = [
      { desc: 'Brand consultation', price: 100, qty: 1, total: 100 },
      { desc: 'logo design', price: 100, qty: 1, total: 100 },
      { desc: 'Website design', price: 100, qty: 1, total: 100 },
      { desc: 'Social media templates', price: 100, qty: 1, total: 100 },
      { desc: 'Brand photography', price: 100, qty: 1, total: 100 },
      { desc: 'Brand guide', price: 100, qty: 1, total: 100 },
    ],
    subtotal = 400,
    tax = 40,
    total = 440,
    signature = 'Adeline Palmerston',
  } = invoicePreviewData || {};

  return (
    <Box
      sx={{
        background: '#FFFFFF',
        border: '1px solid #E8E9E9',
        borderRadius: '8px',
        padding: '32px',
        minHeight: '900px',
        position: 'sticky',
        top: '24px',
      }}
    >
      {/* Invoice Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 4,
          pb: 2,
          borderBottom: '1px solid #E8E9E9',
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: '32px',
              fontFamily: 'Inter',
              fontWeight: 700,
              color: '#192126',
              letterSpacing: '2px',
            }}
          >
            INVOICE
          </Typography>
        </Box>
      </Box>

      {/* Billing Details */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={4}>
          <Grid item xs={6}>
            <Typography
              sx={{
                fontSize: '12px',
                fontFamily: 'Inter',
                fontWeight: 600,
                color: '#667085',
                mb: 1,
                textTransform: 'uppercase',
              }}
            >
              ISSUED TO:
            </Typography>
            <Typography
              sx={{
                fontSize: '14px',
                fontFamily: 'Inter',
                fontWeight: 600,
                color: '#192126',
                mb: 0.5,
              }}
            >
              {issuedTo.name}
            </Typography>
            <Typography
              sx={{
                fontSize: '14px',
                fontFamily: 'Inter',
                fontWeight: 400,
                color: '#192126',
                mb: 0.5,
              }}
            >
              {issuedTo.company}
            </Typography>
            <Typography
              sx={{
                fontSize: '14px',
                fontFamily: 'Inter',
                fontWeight: 400,
                color: '#192126',
              }}
            >
              {issuedTo.address}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography
              sx={{
                fontSize: '12px',
                fontFamily: 'Inter',
                fontWeight: 600,
                color: '#667085',
                mb: 1,
                textTransform: 'uppercase',
              }}
            >
              PAY TO:
            </Typography>
            <Typography
              sx={{
                fontSize: '14px',
                fontFamily: 'Inter',
                fontWeight: 600,
                color: '#192126',
                mb: 0.5,
              }}
            >
              {payTo.bank}
            </Typography>
            <Typography
              sx={{
                fontSize: '14px',
                fontFamily: 'Inter',
                fontWeight: 400,
                color: '#192126',
                mb: 0.5,
              }}
            >
              Account Name: {payTo.accountName}
            </Typography>
            <Typography
              sx={{
                fontSize: '14px',
                fontFamily: 'Inter',
                fontWeight: 400,
                color: '#192126',
              }}
            >
              Account No.: {payTo.accountNumber}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Invoice Info */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between' }}>
        <Box>
          <Typography
            sx={{
              fontSize: '12px',
              fontFamily: 'Inter',
              fontWeight: 600,
              color: '#667085',
              mb: 0.5,
            }}
          >
            INVOICE NO:
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              fontFamily: 'Inter',
              fontWeight: 400,
              color: '#192126',
            }}
          >
            {invoiceNumber}
          </Typography>
        </Box>
        <Box>
          <Typography
            sx={{
              fontSize: '12px',
              fontFamily: 'Inter',
              fontWeight: 600,
              color: '#667085',
              mb: 0.5,
            }}
          >
            DATE:
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              fontFamily: 'Inter',
              fontWeight: 400,
              color: '#192126',
            }}
          >
            {date}
          </Typography>
        </Box>
        <Box>
          <Typography
            sx={{
              fontSize: '12px',
              fontFamily: 'Inter',
              fontWeight: 600,
              color: '#667085',
              mb: 0.5,
            }}
          >
            DUE DATE:
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              fontFamily: 'Inter',
              fontWeight: 400,
              color: '#192126',
            }}
          >
            {dueDate}
          </Typography>
        </Box>
      </Box>

      {/* Description Table */}
      <TableContainer sx={{ mb: 4 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#F8F9FA' }}>
              <TableCell
                sx={{
                  fontSize: '12px',
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  color: '#344054',
                  borderBottom: '1px solid #E8E9E9',
                  textTransform: 'uppercase',
                }}
              >
                DESCRIPTION
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontSize: '12px',
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  color: '#344054',
                  borderBottom: '1px solid #E8E9E9',
                  textTransform: 'uppercase',
                }}
              >
                UNIT PRICE
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontSize: '12px',
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  color: '#344054',
                  borderBottom: '1px solid #E8E9E9',
                  textTransform: 'uppercase',
                }}
              >
                QTY
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontSize: '12px',
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  color: '#344054',
                  borderBottom: '1px solid #E8E9E9',
                  textTransform: 'uppercase',
                }}
              >
                TOTAL
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((row, index) => (
              <TableRow key={index}>
                <TableCell
                  sx={{
                    fontSize: '14px',
                    fontFamily: 'Inter',
                    fontWeight: 400,
                    color: '#192126',
                    borderBottom: '1px solid #E8E9E9',
                  }}
                >
                  {row.desc}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontSize: '14px',
                    fontFamily: 'Inter',
                    fontWeight: 400,
                    color: '#192126',
                    borderBottom: '1px solid #E8E9E9',
                  }}
                >
                  ${row.price}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontSize: '14px',
                    fontFamily: 'Inter',
                    fontWeight: 400,
                    color: '#192126',
                    borderBottom: '1px solid #E8E9E9',
                  }}
                >
                  {row.qty}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontSize: '14px',
                    fontFamily: 'Inter',
                    fontWeight: 400,
                    color: '#192126',
                    borderBottom: '1px solid #E8E9E9',
                  }}
                >
                  ${row.total}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Subtotal Section */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: '14px',
            fontFamily: 'Inter',
            fontWeight: 600,
            color: '#192126',
          }}
        >
          SUBTOTAL
        </Typography>
        <Typography
          sx={{
            fontSize: '14px',
            fontFamily: 'Inter',
            fontWeight: 400,
            color: '#192126',
          }}
        >
          ${subtotal}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: '14px',
            fontFamily: 'Inter',
            fontWeight: 400,
            color: '#192126',
          }}
        >
          Tax 10%
        </Typography>
        <Typography
          sx={{
            fontSize: '14px',
            fontFamily: 'Inter',
            fontWeight: 400,
            color: '#192126',
          }}
        >
          ${tax}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mb: 4,
          pt: 2,
          borderTop: '2px solid #E8E9E9',
        }}
      >
        <Typography
          sx={{
            fontSize: '16px',
            fontFamily: 'Inter',
            fontWeight: 700,
            color: '#192126',
          }}
        >
          TOTAL
        </Typography>
        <Typography
          sx={{
            fontSize: '16px',
            fontFamily: 'Inter',
            fontWeight: 700,
            color: '#192126',
          }}
        >
          ${total}
        </Typography>
      </Box>

      {/* Signature */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 8 }}>
        <Box>
          <Typography
            sx={{
              fontSize: '14px',
              fontFamily: 'Inter',
              fontWeight: 400,
              color: '#192126',
              fontStyle: 'italic',
              borderTop: '1px solid #192126',
              pt: 1,
              minWidth: '150px',
            }}
          >
            {signature}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default InvoicePreview;
